import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertCircle, AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { apiRequest } from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const STATUS_CONFIG = {
  valido: { label: "VALIDO", className: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2, iconColor: "text-emerald-600", description: "Tutti gli elementi obbligatori sono presenti. DVR pronto per la firma." },
  incompleto: { label: "INCOMPLETO", className: "bg-amber-100 text-amber-800 border-amber-300", icon: AlertCircle, iconColor: "text-amber-600", description: "Alcuni elementi raccomandati mancano. Il DVR può essere firmato ma è incompleto." },
  critico: { label: "CRITICO", className: "bg-rose-100 text-rose-800 border-rose-300", icon: AlertTriangle, iconColor: "text-rose-600", description: "Mancano elementi obbligatori. Il DVR NON è pronto per la firma." },
};

const SEVERITY_CONFIG = {
  critico: { label: "Critico", className: "bg-rose-100 text-rose-800 border-rose-300" },
  alto: { label: "Alto", className: "bg-amber-100 text-amber-800 border-amber-300" },
  medio: { label: "Medio", className: "bg-blue-100 text-blue-800 border-blue-300" },
  info: { label: "Info", className: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function DvrValidation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiRequest(`/companies/${id}/dvr-validation`);
      setData(r);
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dvr-validation-loading"><Loader2 className="inline h-4 w-4 animate-spin" /> Caricamento validazione DVR...</div>;
  if (error) return <Alert variant="destructive" data-testid="dvr-validation-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>;
  if (!data) return null;

  const cfg = STATUS_CONFIG[data.overall_status] || STATUS_CONFIG.incompleto;
  const Icon = cfg.icon;
  const missingItems = data.items.filter((it) => !it.present);
  const presentItems = data.items.filter((it) => it.present);

  return (
    <div className="space-y-6" data-testid="dvr-validation-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="dvr-validation-back-button">
          <ArrowLeft className="h-4 w-4" /> Scheda azienda
        </Button>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="dvr-validation-refresh-button">
          <RefreshCw className="h-4 w-4" /> Ricontrolla
        </Button>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dvr-validation-hero">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Validazione DVR</p>
            <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="dvr-validation-title">Conformità del Documento di Valutazione</h2>
            <p className="mt-2 text-slate-600">
              Verifica automatica della presenza dei {data.stats.total} elementi minimi obbligatori secondo D.Lgs. 81/2008 e prassi ProSuite.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={`flex items-center gap-2 border px-3 py-1.5 text-sm font-bold ${cfg.className}`} data-testid="dvr-validation-status-badge">
              <Icon className={`h-5 w-5 ${cfg.iconColor}`} /> {cfg.label}
            </Badge>
            <p className="text-3xl font-bold text-slate-950" data-testid="dvr-validation-score">{data.score}<span className="text-base font-normal text-slate-500">/100</span></p>
          </div>
        </div>
        <Progress value={data.score} className="mt-4 h-3" data-testid="dvr-validation-progress" />
        <p className={`mt-3 text-sm ${cfg.iconColor}`}>{cfg.description}</p>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="dvr-validation-stats">
        <Card className="shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Elementi presenti</p><p className="font-heading text-2xl font-bold text-emerald-700" data-testid="dvr-stat-present">{data.stats.present}/{data.stats.total}</p></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Mancanti critici</p><p className="font-heading text-2xl font-bold text-rose-700" data-testid="dvr-stat-missing-critico">{data.stats.missing_critico}</p></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Mancanti alti</p><p className="font-heading text-2xl font-bold text-amber-700" data-testid="dvr-stat-missing-alto">{data.stats.missing_alto}</p></CardContent></Card>
        <Card className="shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Suggerimenti</p><p className="font-heading text-2xl font-bold text-slate-700" data-testid="dvr-stat-missing-info">{data.stats.missing_medio + data.stats.missing_info}</p></CardContent></Card>
      </section>

      {missingItems.length > 0 && (
        <Card className="shadow-none border-rose-200" data-testid="dvr-missing-card">
          <CardContent className="p-0">
            <div className="border-b border-rose-100 bg-rose-50/40 p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-semibold text-slate-950 flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-rose-600" /> Elementi mancanti ({missingItems.length})</h3>
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {missingItems.map((it) => {
                const sev = SEVERITY_CONFIG[it.severity] || SEVERITY_CONFIG.info;
                return (
                  <li key={it.key} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`dvr-missing-${it.key}`}>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-950" data-testid={`dvr-missing-label-${it.key}`}>{it.label}</p>
                        <Badge className={`border ${sev.className}`} data-testid={`dvr-missing-severity-${it.key}`}>{sev.label}</Badge>
                      </div>
                      <p className="mt-1 text-xs text-slate-500"><strong>Sezione:</strong> {it.section_label}</p>
                      {it.hint && <p className="mt-1 text-sm text-slate-600">{it.hint}</p>}
                    </div>
                    <Button asChild variant="outline" size="sm" data-testid={`dvr-missing-goto-${it.key}`}>
                      <Link to={it.deep_link}>Vai alla sezione <ArrowRight className="h-3.5 w-3.5" /></Link>
                    </Button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {presentItems.length > 0 && (
        <Card className="shadow-none border-emerald-200" data-testid="dvr-present-card">
          <CardContent className="p-0">
            <div className="border-b border-emerald-100 bg-emerald-50/40 p-4">
              <h3 className="font-heading text-xl font-semibold text-slate-950 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Elementi presenti ({presentItems.length})</h3>
            </div>
            <ul className="divide-y divide-slate-100">
              {presentItems.map((it) => (
                <li key={it.key} className="flex items-center justify-between p-3 text-sm" data-testid={`dvr-present-${it.key}`}>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="text-slate-800">{it.label}</span>
                  </div>
                  <span className="text-xs text-slate-500">{it.section_label}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
